/**
 * Declarative settings configuration for the explore tool.
 *
 * This is the single source of truth for:
 *   - Which settings exist and their UI labels/options
 *   - Which viz types support which settings
 *   - Viz-level constraints (locked param values, forced product classes)
 *   - Per-option disable rules (contextual disabling based on current state)
 *   - Side-effect rules (e.g. clearing colorBy when productLevel changes)
 *
 * The CompactSettingsDropdown and ProductClassToggleGroup both read from
 * this config instead of maintaining their own imperative logic.
 */

import {
  ProductClass,
  ProductLevel,
  TradeFlow,
  LocationDetailLevel,
} from "../types/enums";
import {
  UIView,
  VizType,
  ColorBy,
  OverTimeLayoutOption,
  OverTimeOrderingOption,
  HideWorldTradeOption,
  OpportunitySizeOption,
} from "../types/vizEnums";
import { vizParamSchemas } from "./paramSchemas";

// ── Context available to constraint rules ────────────────────────

export interface SettingsContext {
  vizType: VizType;
  uiView: UIView;
  productClass: ProductClass;
  productLevel: ProductLevel;
  colorBy?: ColorBy;
  isWorldToWorld: boolean;
  /** Feasibility in table mode */
  isFeasibilityTable: boolean;
}

// ── Setting option definition ────────────────────────────────────

export interface SettingOption {
  label: string;
  value: string | number;
  /** Return true to disable this option given the current context */
  disabledWhen?: (ctx: SettingsContext) => boolean;
}

// ── Setting definition ───────────────────────────────────────────

export interface SettingConfig {
  /** The URL param key this setting controls */
  paramKey: string;
  /** Display label in the settings panel */
  label: string;
  /** Tooltip explanation shown on hover via an info icon next to the label */
  tooltip?: string | ((ctx: SettingsContext) => string);
  /** Analytics event label (3rd arg to googleAnalyticsCustomEvent) */
  analyticsKey: string;
  /** Available options. Can be static or context-dependent. */
  options: SettingOption[] | ((ctx: SettingsContext) => SettingOption[]);
  /**
   * Which UIView(s) this setting appears in.
   * - undefined = show regardless of view (viz-wide setting)
   * - UIView[] = only show in these views
   */
  showInViews?: UIView[] | ((ctx: SettingsContext) => boolean);
  /**
   * Viz types where this setting should NOT appear, even if the param
   * exists in the schema. Use this when a param is in the schema for
   * data/default purposes but is not user-configurable for that viz.
   */
  excludeFromViz?: VizType[];
  /** Return true to disable the entire group (all items become disabled) */
  groupDisabledWhen?: (ctx: SettingsContext) => boolean;
  /**
   * Side effects when this setting changes.
   * Returns additional param updates to apply alongside the new value.
   */
  sideEffects?: (
    newValue: string | number,
    ctx: SettingsContext,
  ) => Record<string, unknown>;
}

// ── Per-viz constraints (locked values) ──────────────────────────

export interface VizConstraint {
  /** Params locked to a specific value for this viz type */
  lockedParams?: Partial<Record<string, string | number>>;
  /** Product classes allowed (others are disabled in the toggle group) */
  allowedProductClasses?: ProductClass[];
  /** Disable region/subregion selection in location dropdowns */
  disableRegionSelection?: boolean;
  /** Hide the Services Partners entry from location dropdowns */
  hideServicesPartners?: boolean;
}

/**
 * Viz-level constraints that lock params or restrict product classes.
 * Used by ProductClassToggleGroup and other controls.
 */
export const vizConstraints: Partial<Record<VizType, VizConstraint>> = {
  [VizType.ProductSpace]: {
    lockedParams: {
      productClass: ProductClass.HS92Products,
      productLevel: ProductLevel.Product4digit,
    },
    allowedProductClasses: [ProductClass.HS92Products],
    disableRegionSelection: true,
    hideServicesPartners: true,
  },
  [VizType.Feasibility]: {
    disableRegionSelection: true,
    hideServicesPartners: true,
    /** Growth opportunities / feasibility do not support HS 2022; HS22 is disabled in the toggle. */
    allowedProductClasses: [
      ProductClass.HS92Products,
      ProductClass.HS12Products,
      ProductClass.SITCProducts,
    ],
  },
  [VizType.Geo]: {
    lockedParams: {
      view: UIView.Markets,
    },
  },
  [VizType.OverTime]: {
    allowedProductClasses: [
      ProductClass.HS92Products,
      ProductClass.HS12Products,
      ProductClass.HS22Products,
      ProductClass.SITCProducts,
    ],
  },
  [VizType.MarketShare]: {
    allowedProductClasses: [
      ProductClass.HS92Products,
      ProductClass.HS12Products,
      ProductClass.HS22Products,
      ProductClass.SITCProducts,
    ],
  },
};

// ── Setting definitions ──────────────────────────────────────────

/**
 * Master registry of all settings, keyed by param key.
 *
 * Whether a setting appears for a given viz type is determined by
 * checking if the param key exists in that viz type's Zod schema.
 * The `showInViews` / `groupDisabledWhen` further refine visibility.
 */
export const settingsRegistry: Record<string, SettingConfig> = {
  // ── Detail Level (Product) ──────────────────────────────────
  productLevel: {
    paramKey: "productLevel",
    label: "Detail Level",
    tooltip:
      "Visualize data at varying product classification digit levels",
    analyticsKey: "productLevel",
    showInViews: (ctx) => {
      // ProductSpace, Feasibility, MarketShare: show regardless of view
      if (
        [VizType.ProductSpace, VizType.Feasibility, VizType.MarketShare].includes(ctx.vizType)
      ) {
        return true;
      }
      // For Tree/OverTime: only in Products view
      return ctx.uiView === UIView.Products;
    },
    options: (ctx) => {
      // ProductSpace and Feasibility (table mode) lock to 4-digit:
      // show all options but only 4-digit is enabled
      const only4Digit =
        ctx.vizType === VizType.ProductSpace ||
        (ctx.vizType === VizType.Feasibility && ctx.isFeasibilityTable);

      const disableNon4Digit = (value: ProductLevel) =>
        only4Digit && value !== ProductLevel.Product4digit;

      return [
        {
          label: "Section (1 digit)",
          value: ProductLevel.ProductSection,
          disabledWhen: () => disableNon4Digit(ProductLevel.ProductSection),
        },
        {
          label: "2 digit",
          value: ProductLevel.Product2digit,
          disabledWhen: () => disableNon4Digit(ProductLevel.Product2digit),
        },
        {
          label: "4 digit",
          value: ProductLevel.Product4digit,
        },
        {
          label: "6 digit",
          value: ProductLevel.Product6digit,
          disabledWhen: (c) => {
            if (disableNon4Digit(ProductLevel.Product6digit)) return true;
            // SITC doesn't support 6-digit
            if (c.productClass === ProductClass.SITCProducts) return true;
            // Feasibility disables 6-digit always
            if (c.vizType === VizType.Feasibility) return true;
            return false;
          },
        },
      ];
    },
    groupDisabledWhen: (ctx) => {
      // MarketShare locks entire detail level group
      if (ctx.vizType === VizType.MarketShare) return true;
      return false;
    },
    sideEffects: (newValue, ctx) => {
      // Clear complexity colorBy when moving away from 4-digit
      if (
        newValue !== ProductLevel.Product4digit &&
        ctx.colorBy === ColorBy.Complexity
      ) {
        return { colorBy: undefined };
      }
      return {};
    },
  },

  // ── Detail Level (Location — Markets view) ──────────────────
  locationLevel: {
    paramKey: "locationLevel",
    label: "Detail Level",
    tooltip:
      "Visualize data at the regional (less detail), subregional, or country (more detail) level",
    analyticsKey: "locationLevel",
    showInViews: [UIView.Markets],
    options: [
      { label: "Region", value: LocationDetailLevel.region },
      { label: "Subregion", value: LocationDetailLevel.subregion },
      { label: "Country", value: LocationDetailLevel.country },
    ],
  },

  // ── Trade Flow ──────────────────────────────────────────────
  tradeFlow: {
    paramKey: "tradeFlow",
    label: "Trade Flow",
    tooltip:
      "Visualize data by Gross or Net trade flow. Gross refers to exports while net refers to exports minus imports",
    analyticsKey: "tradeFlow",
    // ProductSpace has tradeFlow in its schema but it's not user-configurable
    excludeFromViz: [VizType.ProductSpace],
    options: [
      { label: "Gross", value: TradeFlow.Gross },
      {
        label: "Net",
        value: TradeFlow.Net,
        disabledWhen: (ctx) => ctx.isWorldToWorld,
      },
    ],
    groupDisabledWhen: (ctx) => ctx.vizType === VizType.MarketShare,
  },

  // ── Color By ────────────────────────────────────────────────
  colorBy: {
    paramKey: "colorBy",
    label: "Color By",
    tooltip: (ctx) => {
      if (ctx.vizType === VizType.ProductSpace)
        return "Color points by sector, product complexity, or cluster";
      return "Color cells in the treemap by sector or product complexity";
    },
    analyticsKey: "colorBy",
    showInViews: (ctx) => {
      // ProductSpace: always show (no Products/Markets view concept)
      if (ctx.vizType === VizType.ProductSpace) return true;
      // Treemap: only in Products view
      return ctx.uiView === UIView.Products;
    },
    options: (ctx) => {
      const items: SettingOption[] = [
        { label: "Sector", value: ColorBy.Sector },
        {
          label: "Product Complexity",
          value: ColorBy.Complexity,
          disabledWhen: (c) =>
            c.vizType === VizType.Tree &&
            c.productLevel !== ProductLevel.Product4digit,
        },
      ];
      if (ctx.vizType === VizType.ProductSpace) {
        items.push({ label: "Cluster", value: ColorBy.Cluster });
        items.push({ label: "Green Enablers", value: ColorBy.Green });
      }
      return items;
    },
  },

  // ── OverTime Layout ─────────────────────────────────────────
  layout: {
    paramKey: "layout",
    label: "Layout",
    tooltip: "View trade over time by total value or share",
    analyticsKey: "overtimeLayout",
    options: [
      { label: "Value", value: OverTimeLayoutOption.Value },
      { label: "Share", value: OverTimeLayoutOption.Share },
    ],
  },

  // ── OverTime Ordering ───────────────────────────────────────
  ordering: {
    paramKey: "ordering",
    label: "Ordering",
    tooltip:
      "Order by similar product category (community) or by total volume (totals)",
    analyticsKey: "overtimeOrdering",
    options: [
      { label: "Community", value: OverTimeOrderingOption.Community },
      { label: "Totals", value: OverTimeOrderingOption.Totals },
    ],
  },

  // ── Hide Exports (Feasibility) ──────────────────────────────
  hideExports: {
    paramKey: "hideExports",
    label: "Hide Exports (Spotlight)",
    tooltip:
      "Displays a country's potential production (light coloring) or when turned off, displays a country's current production (dark coloring)",
    analyticsKey: "opportunity_splotlight",
    options: [
      { label: "On", value: HideWorldTradeOption.On },
      { label: "Off", value: HideWorldTradeOption.Off },
    ],
  },

  // ── Sizing (Feasibility, ProductSpace) ──────────────────────
  sizing: {
    paramKey: "sizing",
    label: "Sizing",
    tooltip: "Size product nodes by world trade or uniformly",
    analyticsKey: "sizing",
    options: (ctx) => {
      const items: SettingOption[] = [
        { label: "Uniform", value: OpportunitySizeOption.None },
        { label: "World Trade", value: OpportunitySizeOption.WorldTrade },
      ];
      if (ctx.vizType === VizType.ProductSpace) {
        items.push({
          label: "Country Trade",
          value: OpportunitySizeOption.CountryTrade,
        });
      }
      return items;
    },
  },
};

// ── Settings-param keys (the subset of param keys that are "settings") ──

/**
 * The param keys that represent settings (as opposed to primary selections
 * like exporter, importer, year, etc.).
 */
const settingsParamKeys = new Set(Object.keys(settingsRegistry));

/**
 * Returns the ordered list of setting configs applicable to a given viz type.
 *
 * A setting is included when:
 *   1. Its paramKey exists in the viz type's Zod schema
 *   2. It is not explicitly excluded via `excludeFromViz`
 *
 * Settings with locked values (via `groupDisabledWhen`) are still shown
 * so users can see the current value and understand the constraint.
 */
export function getSettingsForViz(vizType: VizType): SettingConfig[] {
  const schema = vizParamSchemas[vizType];
  const shape = schema.shape as Record<string, unknown>;
  const schemaKeys = new Set(Object.keys(shape));

  return Object.values(settingsRegistry).filter((config) => {
    if (!schemaKeys.has(config.paramKey)) return false;
    if (config.excludeFromViz?.includes(vizType)) return false;
    return true;
  });
}
